var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import isString from 'lodash/isString';
import pLimit from 'p-limit';
import { inject, Lifecycle, scoped } from 'tsyringe';
import { Identity, IdentityInjectionToken } from '../identity/Identity';
import { NetworkNodeFacade } from '../NetworkNodeFacade';
import { StreamIDBuilder } from '../StreamIDBuilder';
import { StreamrClientError } from '../StreamrClientError';
import { StreamRegistry } from '../contracts/StreamRegistry';
import { getExplicitKey, GroupKeyManager } from '../encryption/GroupKeyManager';
import { MessageSigner } from '../signature/MessageSigner';
import { SignatureValidator } from '../signature/SignatureValidator';
import { createLazyMap } from '../utils/Mapping';
import { GroupKeyQueue } from './GroupKeyQueue';
import { MessageFactory } from './MessageFactory';
import { ConfigInjectionToken } from '../ConfigTypes';
const parseTimestamp = (metadata) => {
    if (metadata?.timestamp === undefined) {
        return Date.now();
    }
    else {
        return metadata.timestamp instanceof Date
            ? metadata.timestamp.getTime()
            : isString(metadata.timestamp)
                ? new Date(metadata.timestamp).getTime()
                : metadata.timestamp;
    }
};
let Publisher = class Publisher {
    messageFactories;
    groupKeyQueues;
    concurrencyLimit = pLimit(1);
    node;
    streamRegistry;
    streamIdBuilder;
    identity;
    signatureValidator;
    messageSigner;
    config;
    constructor(node, streamRegistry, groupKeyManager, streamIdBuilder, identity, signatureValidator, messageSigner, config) {
        this.node = node;
        this.streamRegistry = streamRegistry;
        this.streamIdBuilder = streamIdBuilder;
        this.identity = identity;
        this.signatureValidator = signatureValidator;
        this.messageSigner = messageSigner;
        this.config = config;
        this.messageFactories = createLazyMap({
            valueFactory: async (streamId) => {
                return this.createMessageFactory(streamId);
            }
        });
        this.groupKeyQueues = createLazyMap({
            valueFactory: async (streamId) => {
                const explicitKey = await getExplicitKey(streamId, this.streamIdBuilder, this.config.encryption);
                return await GroupKeyQueue.createInstance(streamId, this.identity, groupKeyManager, explicitKey);
            }
        });
    }
    async publish(streamDefinition, content, metadata, deliveryOptions) {
        const timestamp = parseTimestamp(metadata);
        /*
         * There are some steps in the publish process which need to be done sequentially:
         * - message chaining
         * - consuming a group key from a queue
         *
         * It is also good if messages are published to node in the same sequence (within
         * a message chain), as that can avoid unnecessary gap fills: if a subscriber would
         * receive messages m1, m2, m3 in order m1, m3, m2 it would try to get m2 via
         * a gap fill resend before it receives it normally).
         *
         * Currently we limit that there can be only one publish task at any given time.
         * That way message chaining and group keys consuming is done properly. If we want
         * to improve concurrency, we could maybe offload message encryptions to a separate
         * tasks which we'd execute in parallel.
         */
        return this.concurrencyLimit(async () => {
            const [streamId, partition] = await this.streamIdBuilder.toStreamPartElements(streamDefinition);
            try {
                const messageFactory = await this.messageFactories.get(streamId);
                const message = await messageFactory.createMessage(content, {
                    ...metadata,
                    timestamp
                }, partition);
                await this.node.broadcast(message, deliveryOptions);
                return message;
            }
            catch (e) {
                const errorCode = (e instanceof StreamrClientError) ? e.code : 'UNKNOWN_ERROR';
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new StreamrClientError(`Failed to publish to stream ${streamId}. Cause: ${e.message}`, errorCode);
            }
        });
    }
    getGroupKeyQueue(streamId) {
        return this.groupKeyQueues.get(streamId);
    }
    async createMessageFactory(streamId) {
        return new MessageFactory({
            streamId,
            identity: this.identity,
            streamRegistry: this.streamRegistry,
            groupKeyQueue: await this.groupKeyQueues.get(streamId),
            signatureValidator: this.signatureValidator,
            messageSigner: this.messageSigner,
            config: this.config,
        });
    }
};
Publisher = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(4, inject(IdentityInjectionToken)),
    __param(7, inject(ConfigInjectionToken)),
    __metadata("design:paramtypes", [NetworkNodeFacade,
        StreamRegistry,
        GroupKeyManager,
        StreamIDBuilder,
        Identity,
        SignatureValidator,
        MessageSigner, Object])
], Publisher);
export { Publisher };
//# sourceMappingURL=Publisher.js.map